import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AdminLayout } from "@jambushrushti/components/admin/AdminLayout";
import {
  AdminViewToggle,
  useAdminViewMode,
} from "@jambushrushti/components/admin/AdminViewToggle";
import { Card, CardContent } from "@jambushrushti/components/ui/card";
import { Button } from "@jambushrushti/components/ui/button";
import { Input } from "@jambushrushti/components/ui/input";
import { Textarea } from "@jambushrushti/components/ui/textarea";
import { Label } from "@jambushrushti/components/ui/label";
import { Switch } from "@jambushrushti/components/ui/switch";
import { Badge } from "@jambushrushti/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@jambushrushti/components/ui/dialog";
import { useToast } from "@jambushrushti/hooks/use-toast";
import { cmsFetch } from "@jambushrushti/services/adminAuth";
import {
  IMAGE_ACCEPT,
  toMultipart,
  validateImageFile,
} from "@jambushrushti/lib/multipart";
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Search,
  Eye,
  EyeOff,
  Image,
} from "lucide-react";

interface GalleryItem {
  id: string;
  title: string;
  description: string | null;
  image_url: string;
  category: string | null;
  category_division: string | null;
  is_published: boolean | null;
  created_at: string;
}

const GALLERY_DIVISIONS = [
  { value: "gurudev", label: "Gurudev Muni Jambuvijayaji" },
  { value: "legacy", label: "Legacy" },
  { value: "mjrc", label: "MJRC" },
  { value: "temple", label: "Adani Shantigram Jain Temple" },
];

const GALLERY_API_BASE = import.meta.env.DEV
  ? "/jambushrusti-api"
  : (
      import.meta.env.VITE_API_BASE_URL ||
      "https://api.jambushusti.universal-software.in/api"
    ).replace(/\/$/, "");
const GALLERY_ENDPOINT = `${GALLERY_API_BASE}/jambushrusti/cms/media-gallery/`;

async function parseGalleryResponse(response: Response) {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      payload?.detail ||
      payload?.message ||
      payload?.error?.message ||
      payload?.error;
    throw new Error(
      typeof message === "string" ? message : "Gallery request failed."
    );
  }
  return payload;
}

function normalizeGalleryItem(record: Record<string, any>): GalleryItem {
  return {
    id: String(record.id || record.uuid || ""),
    title: String(record.title || ""),
    description: record.description || null,
    image_url:
      record.image || record.image_url || record.media_url || record.url || "",
    category: record.category || null,
    category_division:
      record.gallery_division ||
      record.category_division ||
      record.division ||
      null,
    is_published: Boolean(record.is_published),
    created_at: record.created_at || "",
  };
}

export default function GalleryManagement() {
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useAdminViewMode();
  const [filterDivision, setFilterDivision] = useState<string>("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<GalleryItem | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "",
    category_division: "gurudev",
    is_published: true,
  });

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["admin-gallery"],
    queryFn: async () => {
      const res = await cmsFetch(GALLERY_ENDPOINT, {
        method: "GET",
      });
      const payload = await parseGalleryResponse(res);
      const rows =
        payload?.results || payload?.data?.results || payload?.data || payload;
      return (Array.isArray(rows) ? rows : []).map(normalizeGalleryItem);
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const res = await cmsFetch(GALLERY_ENDPOINT, {
        method: "POST",
        body: toMultipart(
          {
            title: data.title,
            description: data.description,
            category: data.category,
            gallery_division: data.category_division,
            is_published: data.is_published,
          },
          "image",
          imageFile
        ),
      });
      return parseGalleryResponse(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-gallery"] });
      queryClient.invalidateQueries({ queryKey: ["gallery-public"] });
      toast({ title: "Image added successfully!" });
      resetForm();
      setIsDialogOpen(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error adding image",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: typeof formData }) => {
      const res = await cmsFetch(`${GALLERY_ENDPOINT}${id}/`, {
        method: "PATCH",
        body: toMultipart(
          {
            title: data.title,
            description: data.description,
            category: data.category,
            gallery_division: data.category_division,
            is_published: data.is_published,
          },
          "image",
          imageFile
        ),
      });
      return parseGalleryResponse(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-gallery"] });
      queryClient.invalidateQueries({ queryKey: ["gallery-public"] });
      toast({ title: "Image updated successfully!" });
      resetForm();
      setIsDialogOpen(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error updating image",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await cmsFetch(`${GALLERY_ENDPOINT}${id}/`, {
        method: "DELETE",
      });
      return parseGalleryResponse(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-gallery"] });
      queryClient.invalidateQueries({ queryKey: ["gallery-public"] });
      toast({ title: "Image deleted successfully!" });
    },
    onError: (error: Error) => {
      toast({
        title: "Error deleting image",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      category: "",
      category_division: "gurudev",
      is_published: true,
    });
    setEditingItem(null);
    setPreviewUrl("");
    setImageFile(null);
  };

  const handleEdit = (item: GalleryItem) => {
    setEditingItem(item);
    setFormData({
      title: item.title,
      description: item.description || "",
      category: item.category || "",
      category_division: item.category_division || "gurudev",
      is_published: item.is_published ?? true,
    });
    setPreviewUrl(item.image_url);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast({ title: "Title is required", variant: "destructive" });
      return;
    }

    if (!editingItem && !imageFile) {
      toast({ title: "Image file is required", variant: "destructive" });
      return;
    }

    if (editingItem) {
      updateMutation.mutate({
        id: editingItem.id,
        data: formData,
      });
    } else {
      createMutation.mutate(formData);
    }
  };

  const filteredItems = items.filter(item => {
    const matchesSearch =
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.category || "").toLowerCase().includes(searchQuery.toLowerCase());
    const matchesDivision =
      filterDivision === "all" || item.category_division === filterDivision;
    return matchesSearch && matchesDivision;
  });

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="font-display text-xl font-bold text-foreground sm:text-2xl">
              Gallery Management
            </h1>
            <p className="mt-1 text-muted-foreground">
              Add and manage images in the gallery
            </p>
          </div>
          <Dialog
            open={isDialogOpen}
            onOpenChange={open => {
              setIsDialogOpen(open);
              if (!open) {
                resetForm();
              }
            }}
          >
            <DialogTrigger asChild>
              <Button className="w-fit gap-2 md:w-auto">
                <Plus className="h-4 w-4" />
                Add Image
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>
                  {editingItem ? "Edit Image" : "Add New Image"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={e =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                    placeholder="Image title"
                  />
                </div>
                <div>
                  <Label>Image File *</Label>
                  <Input
                    type="file"
                    accept={IMAGE_ACCEPT}
                    onChange={e => {
                      const file = e.target.files?.[0] || null;
                      if (!file) {
                        return setImageFile(null);
                      }
                      try {
                        validateImageFile(file);
                        setImageFile(file);
                        setPreviewUrl(URL.createObjectURL(file));
                      } catch (error) {
                        e.target.value = "";
                        toast({
                          title: (error as Error).message,
                          variant: "destructive",
                        });
                      }
                    }}
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    JPG, PNG, WebP, or GIF. Maximum 5 MB.
                  </p>

                  {/* Image Preview */}
                  {previewUrl && (
                    <div className="mt-3 h-32 w-32 overflow-hidden rounded-lg border">
                      <img
                        src={previewUrl}
                        alt="Preview"
                        className="h-full w-full object-cover"
                        onError={e => {
                          (e.target as HTMLImageElement).src =
                            "/placeholder.svg";
                        }}
                      />
                    </div>
                  )}
                </div>
                <div>
                  <Label>Gallery Division *</Label>
                  <select
                    value={formData.category_division}
                    onChange={e =>
                      setFormData({
                        ...formData,
                        category_division: e.target.value,
                      })
                    }
                    className="w-full rounded-md border bg-background px-3 py-2"
                  >
                    {GALLERY_DIVISIONS.map(div => (
                      <option key={div.value} value={div.value}>
                        {div.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Additional Category/Tag</Label>
                  <Input
                    value={formData.category}
                    onChange={e =>
                      setFormData({ ...formData, category: e.target.value })
                    }
                    placeholder="e.g., Events, Lectures, Archives"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={e =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    placeholder="Image description..."
                    rows={3}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.is_published}
                    onCheckedChange={checked =>
                      setFormData({ ...formData, is_published: checked })
                    }
                  />
                  <Label>Published (visible on website)</Label>
                </div>
                <div className="flex justify-end gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={
                      createMutation.isPending ||
                      updateMutation.isPending ||
                      (!editingItem && !imageFile)
                    }
                  >
                    {(createMutation.isPending || updateMutation.isPending) && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    {editingItem ? "Update Image" : "Add Image"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="flex flex-wrap gap-4">
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search gallery..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <select
            value={filterDivision}
            onChange={e => setFilterDivision(e.target.value)}
            className="rounded-md border bg-background px-4 py-2 text-sm"
          >
            <option value="all">All Divisions</option>
            {GALLERY_DIVISIONS.map(div => (
              <option key={div.value} value={div.value}>
                {div.label}
              </option>
            ))}
          </select>
          <div className="ml-auto">
            <AdminViewToggle value={viewMode} onChange={setViewMode} />
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <div
            className={
              viewMode === "cards"
                ? "grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4"
                : "grid gap-4"
            }
          >
            {filteredItems.length === 0 ? (
              <Card className="col-span-full">
                <CardContent className="py-12 text-center">
                  <Image className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
                  <p className="text-muted-foreground">
                    No images found. Add your first image!
                  </p>
                </CardContent>
              </Card>
            ) : viewMode === "table" ? (
              <div className="overflow-x-auto rounded-xl border bg-card">
                <table className="w-full text-left text-sm">
                  <thead className="border-b bg-muted/60 text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-4 py-3">Image</th>
                      <th className="px-4 py-3">Title</th>
                      <th className="px-4 py-3">Division</th>
                      <th className="px-4 py-3">Category</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredItems.map(item => (
                      <tr key={item.id} className="hover:bg-muted/30">
                        <td className="px-4 py-3">
                          <img
                            src={item.image_url}
                            alt=""
                            className="h-10 w-14 rounded object-cover"
                          />
                        </td>
                        <td className="max-w-xs px-4 py-3 font-medium">
                          <span className="line-clamp-1">{item.title}</span>
                        </td>
                        <td className="px-4 py-3">
                          {GALLERY_DIVISIONS.find(
                            d => d.value === item.category_division
                          )?.label || item.category_division}
                        </td>
                        <td className="px-4 py-3">{item.category || "—"}</td>
                        <td className="px-4 py-3">
                          <Badge
                            variant={
                              item.is_published ? "default" : "secondary"
                            }
                          >
                            {item.is_published ? "Published" : "Draft"}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEdit(item)}
                            >
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-destructive"
                              onClick={() =>
                                confirm(
                                  "Are you sure you want to delete this image?"
                                ) && deleteMutation.mutate(item.id)
                              }
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              filteredItems.map(item => (
                <Card key={item.id} className="group overflow-hidden">
                  <div className="relative aspect-square bg-muted">
                    <img
                      src={item.image_url}
                      alt={item.title}
                      className="h-full w-full object-cover"
                      onError={e => {
                        (e.target as HTMLImageElement).src = "/placeholder.svg";
                      }}
                    />
                    <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/60 opacity-0 transition-opacity group-hover:opacity-100">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => handleEdit(item)}
                      >
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => {
                          if (
                            confirm(
                              "Are you sure you want to delete this image?"
                            )
                          ) {
                            deleteMutation.mutate(item.id);
                          }
                        }}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                    <div className="absolute right-2 top-2">
                      <Badge
                        variant={item.is_published ? "default" : "secondary"}
                        className="text-xs"
                      >
                        {item.is_published ? (
                          <Eye className="h-3 w-3" />
                        ) : (
                          <EyeOff className="h-3 w-3" />
                        )}
                      </Badge>
                    </div>
                  </div>
                  <CardContent className="p-3">
                    <h3 className="line-clamp-1 text-sm font-medium">
                      {item.title}
                    </h3>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {item.category_division && (
                        <Badge variant="default" className="text-xs">
                          {GALLERY_DIVISIONS.find(
                            d => d.value === item.category_division
                          )?.label || item.category_division}
                        </Badge>
                      )}
                      {item.category && (
                        <Badge variant="outline" className="text-xs">
                          {item.category}
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
